import React, { Component, ReactNode, ErrorInfo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  Chip,
  Divider,
} from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  Keyboard as KeyboardIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { KonnectAnalytics } from 'KonnectAnalytics/konnectAnalytics';
import { captureSentryException, setSentryContext } from 'services/sentry';
import { palette, animations } from '../../theme';
import i18n from '../../services/i18n';

type ErrorBoundaryProps = {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  errorMessage?: string;
  errorTimestamp?: string;
  userTimezone?: string;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    });

    // Get user's timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.setState({
      hasError: true,
      errorMessage: error.message,
      errorTimestamp: timestamp,
      userTimezone: userTimezone,
    });

    KonnectAnalytics.sendErrorLog('Error', error, errorInfo);
    try {
      setSentryContext('errorInfo', {
        componentStack: errorInfo.componentStack,
        timestamp: timestamp,
        userTimezone: userTimezone,
      });
      captureSentryException(error);
    } catch (sentryError) {
      console.error('Sentry error reporting failed:', sentryError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: palette.background.default,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 3,
            ...animations.fadeInEffect,
          }}>
          <Card
            sx={{
              maxWidth: 600,
              width: '100%',
              boxShadow: '0 8px 32px rgba(46, 161, 248, 0.1)',
              borderRadius: '16px',
              border: `1px solid ${palette.grey[200]}`,
            }}>
            <CardContent sx={{ padding: 4 }}>
              {/* Header with Error Icon */}
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
                <ErrorIcon
                  sx={{
                    fontSize: 48,
                    color: palette.error.main,
                    marginRight: 2,
                  }}
                />
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      color: palette.secondary.main,
                      fontWeight: 'bold',
                      marginBottom: 0.5,
                    }}>
                    {i18n.t('errorBoundary.title', 'Oops! Something went wrong')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: palette.text.secondary }}>
                    {i18n.t(
                      'errorBoundary.subtitle',
                      'An unexpected error occurred in the application'
                    )}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ marginY: 3 }} />

              {/* Error Details */}
              <Alert
                severity="error"
                icon={<InfoIcon />}
                sx={{
                  marginBottom: 3,
                  backgroundColor: palette.error.light + '10',
                  border: `1px solid ${palette.error.light}`,
                  borderRadius: '12px',
                }}>
                <AlertTitle sx={{ fontWeight: 'bold' }}>
                  {i18n.t('errorBoundary.errorDetailsTitle', 'Error Details')}
                </AlertTitle>
                <Typography variant="body2" sx={{ marginTop: 1 }}>
                  {this.state.errorMessage
                    ? i18n.t('errorBoundary.technicalInfo', 'Technical information: {{message}}', {
                        message: this.state.errorMessage,
                      })
                    : i18n.t(
                        'errorBoundary.genericMessage',
                        'A technical error occurred. Our team has been notified about this issue.'
                      )}
                </Typography>
                {this.state.errorTimestamp && (
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: 1,
                      fontWeight: 'bold',
                      color: palette.text.primary,
                    }}>
                    {i18n.t('errorBoundary.errorTime', 'Error occurred at: {{timestamp}}', {
                      timestamp: this.state.errorTimestamp,
                    })}
                  </Typography>
                )}
                {this.state.userTimezone && (
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: 0.5,
                      fontStyle: 'italic',
                      color: palette.text.secondary,
                    }}>
                    {i18n.t('errorBoundary.timezone', 'User timezone: {{timezone}}', {
                      timezone: this.state.userTimezone,
                    })}
                  </Typography>
                )}
              </Alert>

              {/* Refresh Instructions */}
              <Box sx={{ marginBottom: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: palette.secondary.main,
                    fontWeight: 'bold',
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <KeyboardIcon sx={{ marginRight: 1, color: palette.primary.main }} />
                  {i18n.t('errorBoundary.howToFix', 'How to fix:')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', marginBottom: 2 }}>
                  <Chip
                    label="F5"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: palette.primary.main,
                      color: palette.primary.main,
                      fontWeight: 'bold',
                    }}
                  />
                  <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                    {i18n.t('errorBoundary.or', 'or')}
                  </Typography>
                  <Chip
                    label="Ctrl + R"
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: palette.primary.main,
                      color: palette.primary.main,
                      fontWeight: 'bold',
                    }}
                  />
                  <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                    {i18n.t('errorBoundary.refreshInstruction', 'to refresh the page')}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: palette.text.secondary,
                    fontStyle: 'italic',
                  }}>
                  💡{' '}
                  {i18n.t(
                    'errorBoundary.tip',
                    'Tip: Refreshing the page solves most temporary issues'
                  )}
                </Typography>
              </Box>

              {/* Footer */}
              <Box sx={{ marginTop: 4, textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: palette.text.secondary,
                    fontSize: '12px',
                  }}>
                  {i18n.t(
                    'errorBoundary.supportMessage',
                    'If the problem persists, please make screenshot of that error and contact support'
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
